import { format, formatISO, subDays } from "date-fns";
import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Table, Button, Form, Spinner, Card } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { TsAdminContext } from "../../context/ts-admin";
import downloadIcon from "../../assets/icons/download.svg";
import leftButton from "../../assets/icons/jump_left.svg";
import rightButton from "../../assets/icons/jump_right.svg";
import leftButtonEnable from "../../assets/icons/jump_left_enable.svg";
import rightButtonEnable from "../../assets/icons/jump_right_enable.svg";
import { IconArrow } from "../../parts/icon";
import {AccessControlLogData, downloadErrorLog, getAccessControlLog} from "../../api-service/storage-provider";
import {apiDownloadCsv} from "../../api-service/api";
import { toast } from "react-toastify";

const tableHeaders = [
	{ label: "日時", key: "createdAt" },
	{ label: "種類", key: "action" },
	{ label: "件数", key: "success_count" },
	{ label: "失敗件数", key: "error_count" },
	{ label: "ログレベル", key: "status" },
	{ label: "内容", key: "message" },
];

const actionMap = {
	s3_data_fetch_action: 'データ取得',
	sf_data_send_action: 'データ送信',
	sf_data_send_manual_action: 'データ送信（手動）',
	import_emp_from_device: '端末社員データ取り込み',
}

const AccessControlLog: React.FC = () => {
	const MAX_LOGS_PER_PAGE = 20;
	const [orgInfo] = useContext(TsAdminContext);
	const [timeSearch , setTimeSearch] = useState(new Date());
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingDownload, setIsLoadingDownload] = useState<Set<string>>(new Set());
	const [searchData, setSearchData] = useState({
		startDate: subDays(new Date(), 3),
		endDate: new Date(),
		status: "",
	});
	const [listLogs, setListLogs] = useState<AccessControlLogData[]>([]);

	const [currentPage, setCurrentPage] = useState(1);
	const changeStarDate = (date: any) => {
		setSearchData({
			...searchData,
			startDate: date
		})
	}
	const changeEndDate = (date: any) => {
		setSearchData({
			...searchData,
			endDate: date
		})
	}

	const generateCsv = () => {
		const headers = tableHeaders.map(header => `"${header.label}"`).join(",");
		const rows = listLogs.map(log => {
			return tableHeaders.map(header => {
				let value = (log)[header.key];
				if (header.key === "createdAt") {
					value = format(new Date(value), `yyyy/MM/dd HH:mm`);
				}
				if (header.key === "status") {
					value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
				}
				if (header.key === "action") {
					value = actionMap[value] || value
				}
				if (header.key === "success_count" || header.key === "error_count") {
					value = value || '0';
				}
				return `"${value !== undefined && value !== null ? String(value).replace(/"/g, '""') : ""}"`;
			}).join(",");
		}).join("\n");
		const blob = new Blob(["\uFEFF" + headers + "\n" + rows], {
			type: "text/csv;charset=utf-8;",
		});

		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute(
			"download",
			`AccessControlLog-${format(timeSearch, "yyyy_MM_dd HH_mm")}.csv`
		);
		link.click();
		URL.revokeObjectURL(url);
	};

	const fetchAccessControlLog = React.useCallback(async () => {
		setIsLoading(true);
		setTimeSearch(new Date());
		setCurrentPage(1);
		const payload = {
			startDate: formatISO(searchData.startDate, { representation: "date" }),
			endDate: formatISO(searchData.endDate, { representation: "date" }),
			status: searchData.status,
			TargetOrg: `${localStorage.getItem('target-org') || orgInfo?.currentOrg?.OrgId}`,
		};
		// Fetch logs from API or other sources here
		const result = await getAccessControlLog(payload); // Simulate API call
		if (result && result.data) {
			setListLogs(result.data.sort((a, b) => (new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())));
		}
		setIsLoading(false);
	}, [searchData, orgInfo]);


	const downloadLog = async ({ item }: { item: AccessControlLogData }) => {	
		setIsLoadingDownload(prev => new Set(prev).add(item.key || ''));
		const payload = {
			key: item?.key,
			TargetOrg: `${localStorage.getItem("target-org") || orgInfo?.currentOrg?.OrgId}`,
		};
		try {
			const response = await apiDownloadCsv(`/batch-job/storage-provider/download-logs`, payload) as any;
			console.log('response:', response);
			const u8 = new Uint8Array(response.data);
			let blobData: BlobPart;
			if (u8.length >= 2 && u8[0] === 0x50 && u8[1] === 0x4B) {
				blobData = u8;
			} else {
				const base64Text = new TextDecoder().decode(u8);
				const binaryString = atob(base64Text);
				const bytes = new Uint8Array(binaryString.length);
				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i);
				}
				blobData = bytes;
			}
			const blob = new Blob([blobData], {type: "application/zip"});
			const url = window.URL.createObjectURL(blob);


			const a = document.createElement("a");
			a.href = url;
			a.download = `${item.key}.zip`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			// toast.error(error)
			console.log("Download error:", JSON.stringify(error));
		} finally {
			setIsLoadingDownload(new Set());
		}
	};

	useEffect(() => {
		fetchAccessControlLog();
	}, []);

	return (
		<div className="p-4 access-control-log flex-grow-1">
			<Card className="card-container">
				<Card.Body className="page-title">ログ確認</Card.Body>
			</Card>

			{/* Filter Form */}
			<div className="mt-3">
				<Form>
					<Row xs={1} md={3} lg={5} className="d-flex align-items-center p-3">
						<Col xs={12} md={8} lg={4} className="d-flex align-items-center">
							<Form.Label className="mt-2 me-2 log-form-label" htmlFor="startDate" style={{
								fontSize: '12px',
								minWidth: 'max-content'
							}}>
								期間
							</Form.Label>
							<ReactDatePicker
								selected={searchData.startDate}
								maxDate={new Date()}
								dateFormat={"yyyy/MM/dd"}
								onFocus={e => e.target.blur()}
								className="form-control log-form-control"
								customInput={
									<input

										type="text"
										readOnly={true}
										id="startDate"
										placeholder=""
									/>
								}
								onChange={changeStarDate}
							/>
							<span className="px-2">〜</span>
							<ReactDatePicker
								selected={searchData.endDate}
								maxDate={new Date()}
								minDate={searchData.startDate}
								className="form-control log-form-control"
								dateFormat={"yyyy/MM/dd"}
								onFocus={e => e.target.blur()}
								customInput={
									<input
										readOnly={true}
										type="text"
										id="endDate"
										placeholder=""
									/>
								}
								onChange={changeEndDate}
							/>
						</Col>
						<Col xs={12} md={7} lg="auto" className="d-flex align-items-center">
							<Form.Label className="mt-2 me-2 log-form-label" htmlFor="inlineFormInputGroup">
								ログレベル
							</Form.Label>
							<Form.Check className="log-form-control pe-2 mb-0 radio-form-check"
							>
								<Form.Check.Input
									value=""
									type="radio"
									name="log-level"
									id="log-level-all"
									className="me-2"
									checked={searchData.status === ""}
									onChange={e => setSearchData({ ...searchData, status: e.target.value })}
								/>
								<Form.Check.Label htmlFor="log-level-all">全て</Form.Check.Label>
							</Form.Check>
							<Form.Check className="log-form-control pe-2 mb-0 radio-form-check">
								<Form.Check.Input
									value="info"
									type="radio"
									name="log-level"
									id="log-level-info"
									className="me-2"
									checked={searchData.status === "info"}
									onChange={e => setSearchData({ ...searchData, status: e.target.value })}
								/>
								<Form.Check.Label htmlFor="log-level-info" className="info-level">Info</Form.Check.Label>
							</Form.Check>
							<Form.Check className="log-form-control pe-2 mb-0 radio-form-check"
							>
								<Form.Check.Input
									value="error"
									type="radio"
									name="log-level"
									id="log-level-error"
									className="me-2"
									checked={searchData.status === "error"}
									onChange={e => setSearchData({ ...searchData, status: e.target.value })}
								/>
								<Form.Check.Label htmlFor="log-level-error" className="error-level">Error</Form.Check.Label>
							</Form.Check>
						</Col>
						<Col xs="auto" md="auto" lg="auto" className="align-items-center">
							{
								isLoading ?
									<Button size={'sm'} className="button-search" disabled>
										<Spinner
											as="span"
											animation="grow"
											size="sm"
											role="status"
											aria-hidden="true"
										/>
										検索...
									</Button>
									:
									<Button size={'sm'} className="button-search" onClick={fetchAccessControlLog}>
										検索
									</Button>
							}
						</Col>
						<Col xs="auto" md="auto" lg="auto" className="ms-auto d-flex justify-content-end">
							<Button
								variant="outline-primary bg-white button-download-csv"
								onClick={generateCsv}
								disabled={isLoading || listLogs.length === 0}
							>
								<img src={downloadIcon} alt="info" />
								<span style={{ marginLeft: '8px' }}>CSVダウンロード</span>
							</Button>
						</Col>
					</Row>
				</Form>
			</div>

			{/* Log Table */}
			{listLogs && listLogs.length > 0 ?
				<Table className="mt-3 rounded-0 table-logs">
					<thead>
						<tr>
							<th className="ms-column">日時</th>
							<th className="ms-column">種類</th>
							<th className="xs-column">件数</th>
							<th className="s-column">失敗件数</th>
							<th className="s-column">ログレベル</th>
							<th>内容</th>
							<th className="ms-column"></th>
						</tr>
					</thead>
					<tbody>
						{listLogs.slice((currentPage - 1) * MAX_LOGS_PER_PAGE, currentPage * MAX_LOGS_PER_PAGE).map((log, idx) => (
							<tr key={idx}>
								<td className="align-middle text-start">
									<p className="mb-0">
										{format(new Date(log.createdAt), `yyyy/MM/dd`)} {format(new Date(log.createdAt), `HH:mm`)}
									</p>
								</td>
								<td className="align-middle text-start">
									<p className="mb-0">{actionMap[log.action] || log.action}</p>
								</td>
								<td className="align-middle text-start">
									<p className="mb-0">{log.success_count}</p>
								</td>
								<td className="align-middle text-start">
									<p className="mb-0">{log.error_count}</p>
								</td>
								<td className="align-middle text-start">
									{log.status.toLowerCase() === "info" ? (
										<span className="info-level">Info</span>
									) : (
										<span className="error-level">Error</span>
									)}
								</td>
								<td className="align-middle text-start" style={{ wordBreak: "break-all" }}>
									{Array.isArray(log.message) && log.message.length > 0 ? log.message.map((message, index) => (
										<p className="mb-0" key={index}>{message}</p>
									)) : (
										<p className="mb-0">{log.message}</p>
									)}
								</td>
								<td style={{wordBreak: "break-all"}}>
									{
										log.status.toLowerCase() !== "info" && !!log.key &&
										<button type="button"
												disabled={isLoadingDownload.has(log.key || '')}
												onClick={() => downloadLog({ item: log })}
												className="border px-4 btn-save btn btn-primary btn-sm">エラーデータを<br/>ダウンロード
										</button>
									}
								</td>
							</tr>
						))}
					</tbody>
				</Table>
				: <h6 className="text-center pt-5 fw-bold">{!isLoading ? 'データがありません。' : '検索中・・・・'}</h6>
			}

			{/* Pagination */}
			{
				listLogs && listLogs.length > 0 && <div>
					<ul className="pagination justify-content-center">
						<li className={currentPage === 1 ? "page-item disabled" : "page-item"}>
							<a className="page-link" onClick={() => setCurrentPage(1)}>
								{currentPage === 1 ?
									<img src={leftButton} />
									:
									<img src={leftButtonEnable} />
								}
							</a>
						</li>
						<li className={currentPage === 1 ? "page-item disabled" : "page-item"}>
							<a className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
								{currentPage === 1 ?
									<IconArrow type='left' />
									:
									<IconArrow type='left' active={true} />
								}
							</a>
						</li>
						<li className="pt-2">（{currentPage}/{Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE)}）</li>
						<li className={currentPage === Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE) ? "page-item disabled" : "page-item"}>
							<a className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
								{currentPage === Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE) ?
									<IconArrow type='right' />
									:
									<IconArrow type='right' active={true} />
								}
							</a>
						</li>
						<li className={currentPage === Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE) ? "page-item disabled" : "page-item"}>
							<a className="page-link"
								onClick={() => setCurrentPage(Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE))}>
								{currentPage === Math.ceil(listLogs.length / MAX_LOGS_PER_PAGE) ?
									<img src={rightButton} />
									:
									<img src={rightButtonEnable} />
								}
							</a>
						</li>
					</ul>
				</div>
			}
		</div>
	);
};

export default AccessControlLog;
